import _                        from 'underscore';
import { useReducer, useState } from 'react';
import { produce }              from 'immer';
import { reactLocalStorage }    from 'reactjs-localstorage';

import { STRING_CONVERSION }                                 from '../constants/FormatStringEnums';
import { EnvironmentObject }                                 from '../environments/types/base';
import { KeyedObject, ReactTableV8OptionalTableState, Uuid } from '../types';
import { getKeyByValue }                                     from '../util';
import { UI_DISPLAY_MODES }                                  from '../constants/UIEnums';

//@ts-ignore
let environment: EnvironmentObject = __ENV__;

const suffixDelimiter = STRING_CONVERSION.DELIMITER;

enum ACTIONS {
    GET    = 'get',
    UPDATE = 'update',
    RESET  = 'reset',
    CLEAR  = 'clear',
}

export type PrefStateHook = [ PrefState, PrefStateHookActions, PrefEnumList ];
export type PrefKey = string | keyof PreferenceStateType;
export type PrefState = null | {} | PreferenceStateType | { [ key: string ]: any };
export type PrefEnumList = {
    [key in keyof PreferenceStateType]: key;
};
export type PrefStateHookActions = {
    [ ACTIONS.GET ]: PrefStateGetActionFunc,
    [ ACTIONS.UPDATE ]: PrefStateUpdateActionFunc,
    [ ACTIONS.RESET ]: PrefStateResetActionFunc,
    [ ACTIONS.CLEAR ]: PrefStateClearActionFunc,
}

export interface PrefStateGetActionFunc {( pref: keyof PreferenceStateType, debug?: boolean, beforeAction?: ( ( value: any ) => PreferenceStateType[ keyof PreferenceStateType ] ) ): any;};

export interface PrefStateUpdateActionFunc {( pref: keyof PreferenceStateType, prefValue: any, debug?: boolean, beforeAction?: ( ( updateValue: any, updateChanges?: any ) => PreferenceStateType[ keyof PreferenceStateType ] ) ): void;};

export interface PrefStateResetActionFunc {( debug?: boolean ): void;};

export interface PrefStateClearActionFunc {( debug?: boolean ): void;};

export interface PrefStateAction {
    type: ACTIONS,
    value: {
        pref: keyof PreferenceStateType,
        prefValue: PreferenceStateType[ keyof PreferenceStateType ],
    },
    debug: boolean,

    beforeAction?( updateValue: any, updateChanges?: any ): PreferenceStateType[ keyof PreferenceStateType ] | undefined
}

export type PreferenceStateType = {

    /* Has the user agreed to terms / conditions / policies */
    AGREED_TO_USAGE_TERMS: boolean,

    /* Which tab in the DEM to show for intake questions area */
    ADMIN_INTAKE_QUESTIONS: number,

    /* Which tab in the DEM to show for quetion edit area */
    ADMIN_INTAKE_QUESTIONS_EDIT: number,

    /* Which tab in the DEM to show for objectives area */
    ADMIN_OBJECTIVES: number,

    /* Which tab in the DEM to show for objective edit area */
    ADMIN_OBJECTIVES_SUBOBJECTIVES_EDIT: number,

    /* Which tab in the DEM to show for activities area */
    ADMIN_ACTIVITIES: number,

    /* Which tab in the DEM to show for activity edit area */
    ADMIN_ACTIVITIES_EDIT: number,

    /* Which tab in the DEM to show for categories area */
    ADMIN_CATEGORIES: number,

    /* Which tab in the DEM to show for resources area */
    ADMIN_RESOURCES: number,

    /* Which tab for professional fees in the DEM to show for resources area */
    ADMIN_RESOURCES_FEES: number,

    /* Which tab for hire types in the DEM to show for resources area */
    ADMIN_RESOURCES_HIRE_TYPES: number,

    /* Which tab for business functions in the DEM to show for resources area */
    ADMIN_RESOURCES_BUSINESS_FUNCTIONS: number,

    /* Which tab in the DEM to show for assumptions area */
    ADMIN_ASSUMPTIONS: number,

    /* Which tab in the DEM to show for assumption group edit area */
    ADMIN_ASSUMPTION_GROUPS_EDIT: number,

    /* Which tab in the DEM to show for orgs / projects area */
    ADMIN_ORGANIZATIONS: number,

    /* Which tab in the DEM to show for organization edit area */
    ADMIN_ORGANIZATIONS_EDIT: number,

    /* Which tab in the DEM to show for project edit area */
    ADMIN_ORGANIZATIONS_PROJECT_EDIT: number,

    /* Which tab in the DEM to show for user area */
    ADMIN_USERS: number,

    /* Which tab in the DEM to show for user edit area */
    ADMIN_USERS_EDIT: number,

    /* Which project was the user in last */
    PROJECT_ID?: string,

    /* Which project version was the user in last */
    PROJECT_VERSION_ID?: number,

    /* Which area of the application was the user in last */
    PROJECT_SECTION?: string,

    /* Which area of the application was the user in last, as a subsection */
    PROJECT_SUBSECTION?: object,

    /* Which sections of the inquiry survey should be open  */
    SURVEY_DISCOVERY_SECTION: string[],

    /* Which section of the assumptions area should be displayed  */
    ASSUMPTIONS_REVIEW_SECTION: number,

    /* Which tab of the assumptions timeline area should be displayed  */
    ASSUMPTIONS_TIMELINE_VIEW: number,

    /* Which sections of the "view all assumptions" area should be displayed  */
    ASSUMPTIONS_ALL_ITEMS_VIEW: number,

    /* Which sections of the "view assumptions by category" area should be displayed  */
    ASSUMPTIONS_ALL_ITEMS_BY_CAT_ACTIVE: string[],

    /* Which section of the objectives area should be displayed  */
    OBJECTIVES_REVIEW_SECTION: number,

    /* Which sections of the "view all objectives by category" area should be displayed  */
    OBJECTIVES_ALL_ITEMS_BY_CAT_ACTIVE: string[],

    /* Which items of the "view all objectives by category" area should be actively filtered  */
    OBJECTIVES_ALL_ITEMS_BY_CAT_ACTIVE_FILTERS: string[],

    /* Which sections of the "view all objectives by group" area should be displayed  */
    OBJECTIVES_ALL_ITEMS_BY_GROUP_ACTIVE: string[],

    /* Which sections of the "view all objectives by year" area should be displayed  */
    OBJECTIVES_ALL_ITEMS_BY_YEAR_ACTIVE: string[],

    /* Which categories of the objective roadmap area should be displayed  */
    OBJECTIVES_ROADMAP_ACTIVE_CATEGORIES: string[],

    /* Which filters of the objective roadmap area should be active  */
    OBJECTIVES_ROADMAP_ACTIVE_FILTERS: any,

    /* Should we suppress the warning when removing objectives from the project */
    OBJECTIVES_SUPPRESS_REMOVAL_WARNING: boolean,

    /* Should we suppress the warning when reverting objective timing from the project */
    OBJECTIVES_SUPPRESS_REVERSION_WARNING: boolean,

    /* Which filters of the objective roadmap area should be active  */
    OBJECTIVES_ROADMAP_TIMEFRAME_DATES: [ string, string ],

    /* Which filters of the objective roadmap area should be open to quarter view  */
    OBJECTIVES_ROADMAP_YEARS_IN_QUARTER_VIEW: number[],

    /* Which sections of the "objective selection by subcategory" area should be displayed  */
    OBJECTIVES_SELECTION_ALL_ITEMS_BY_SUBCAT_ACTIVE: string[],

    /* Activity map start / end dates to base last map view on */
    MAP_TIMEFRAME_DATES: [ string, string ],

    /* Should activity map only display activities active in the given timeframe */
    MAP_COMPACT_CATEGORIES: boolean,

    /* Which categories of the activity map area should be displayed  */
    MAP_ACTIVE_CATEGORIES: string[],

    /* Which filters of the activity map area should be active  */
    MAP_ACTIVE_FILTERS: any,

    /* Which tab of user details edit area should be displayed */
    USER_DETAILS_SECTION: number,

    /* Which years are should be active on budget by hire review */
    BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_YEARS: number[],

    /* Which filters of the budget by hire area should be active  */
    BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_FILTERS: string,

    /* Whether to display headcounts on the budget sheet or not */
    BUDGET_VISUALIZATION_BY_HIRE_HEADCOUNTS_VISIBLE: boolean,

    /* Which years are should be active on budget by category review */
    BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_YEARS: number[],

    /* Which filters of the budget by category area should be active  */
    BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_FILTERS: string,

    /* Which filters of the budget by category area should be active  */
    BUDGET_VISUALIZATION_BY_CATEGORY_EXPANDED_ROWS: string[],

    /* Whether to display headcount on the budget sheet or not */
    BUDGET_VISUALIZATION_BY_HEADCOUNT_HEADCOUNTS_VISIBLE: boolean,

    /* Whether to display custom budget items on the budget sheet or not */
    BUDGET_VISUALIZATION_CUSTOM_BUDGET_ITEMS_VISIBLE: boolean,

    /* Whether to display regular budget items on the budget sheet or not */
    BUDGET_VISUALIZATION_BUDGET_ITEMS_VISIBLE: boolean,

    /* Whether to display regular budget items that represent professional fees on the budget sheet or not */
    BUDGET_VISUALIZATION_BUDGET_PROFESSIONAL_FEE_ITEMS_VISIBLE: boolean,

    /* Whether to display regular budget items that represent operational costs on the budget sheet or not */
    BUDGET_VISUALIZATION_BUDGET_OPERATIONAL_COST_ITEMS_VISIBLE: boolean,

    /* Whether to display regular budget items that represent scheduled cost on the budget sheet or not */
    BUDGET_VISUALIZATION_BUDGET_SCHEDULED_COST_ITEMS_VISIBLE: boolean,

    /* Whether to display regular budget items that represent ongoing costs on the budget sheet or not */
    BUDGET_VISUALIZATION_BUDGET_ONGOING_COST_ITEMS_VISIBLE: boolean,

    /* Whether to display regular budget items that represent recurrences
     originating from another budget item on the budget sheet or not */
    BUDGET_VISUALIZATION_BUDGET_RECURRING_PROFESSIONAL_FEE_ITEMS_VISIBLE: boolean,
    BUDGET_VISUALIZATION_BUDGET_RECURRING_OPERATIONAL_COST_ITEMS_VISIBLE: boolean,
    // BUDGET_VISUALIZATION_BUDGET_RECURRING_ONGOING_COST_ITEMS_VISIBLE: boolean,

    /* Which tab of the hiring manager should be active  */
    HIRE_MANAGER_TAB: number,

    /* Whether to display in minimized chrome view or not */
    UI_DISPLAY_MODES: UI_DISPLAY_MODES[],

    /* Which tab of project management details edit area should be displayed */
    PROJECT_MANAGMENT_DETAILS_SECTION: number,

    /* Which section of the project planning area should be displayed  */
    PROJECT_PLANNING_REVIEW_SECTION: number,

    /* Table state for activity searching tools */
    TOOLS_ACTIVITY_SEARCH_TABLE_STATE: ReactTableV8OptionalTableState,

    /* Global state for projects that are visible user has access to. */
    GLOBAL_ACTIVE_PROJECT_VISIBILITY: Uuid[],
}
export type PreferenceDictionaryType = Record<keyof PreferenceStateType, { hashId: string, default: any }>

export const defaultCorvalPreferences: PreferenceDictionaryType = {
    AGREED_TO_USAGE_TERMS:                                                { hashId: 'P0', default: false },
    ADMIN_INTAKE_QUESTIONS:                                               { hashId: 'P1', default: 0 },
    ADMIN_INTAKE_QUESTIONS_EDIT:                                          { hashId: 'P2', default: 0 },
    ADMIN_OBJECTIVES:                                                     { hashId: 'P3', default: 0 },
    ADMIN_OBJECTIVES_SUBOBJECTIVES_EDIT:                                  { hashId: 'P4', default: 0 },
    ADMIN_ACTIVITIES:                                                     { hashId: 'P5', default: 0 },
    ADMIN_ACTIVITIES_EDIT:                                                { hashId: 'P6', default: 0 },
    ADMIN_ASSUMPTIONS:                                                    { hashId: 'P7', default: 0 },
    ADMIN_ASSUMPTION_GROUPS_EDIT:                                         { hashId: 'P8', default: 0 },
    ADMIN_ORGANIZATIONS:                                                  { hashId: 'P9', default: 0 },
    ADMIN_ORGANIZATIONS_EDIT:                                             { hashId: 'P10', default: 0 },
    ADMIN_ORGANIZATIONS_PROJECT_EDIT:                                     { hashId: 'P11', default: 0 },
    ADMIN_USERS:                                                          { hashId: 'P12', default: 0 },
    ADMIN_USERS_EDIT:                                                     { hashId: 'P13', default: 0 },
    PROJECT_SECTION:                                                      { hashId: 'P14', default: null },
    SURVEY_DISCOVERY_SECTION:                                             { hashId: 'P15', default: [] },
    ASSUMPTIONS_REVIEW_SECTION:                                           { hashId: 'P16', default: 1 },
    ASSUMPTIONS_TIMELINE_VIEW:                                            { hashId: 'P17', default: 0 },
    ASSUMPTIONS_ALL_ITEMS_VIEW:                                           { hashId: 'P18', default: 0 },
    ASSUMPTIONS_ALL_ITEMS_BY_CAT_ACTIVE:                                  { hashId: 'P19', default: null },
    OBJECTIVES_REVIEW_SECTION:                                            { hashId: 'P20', default: 1 },
    OBJECTIVES_ALL_ITEMS_BY_CAT_ACTIVE:                                   { hashId: 'P21', default: [] },
    OBJECTIVES_ALL_ITEMS_BY_GROUP_ACTIVE:                                 { hashId: 'P22', default: [] },
    OBJECTIVES_ALL_ITEMS_BY_YEAR_ACTIVE:                                  { hashId: 'P23', default: [] },
    MAP_TIMEFRAME_DATES:                                                  { hashId: 'P24', default: [ null, null ] },
    MAP_ACTIVE_CATEGORIES:                                                { hashId: 'P25', default: null },
    MAP_ACTIVE_FILTERS:                                                   { hashId: 'P26', default: null },
    USER_DETAILS_SECTION:                                                 { hashId: 'P27', default: 0 },
    MAP_COMPACT_CATEGORIES:                                               { hashId: 'P28', default: false },
    ADMIN_CATEGORIES:                                                     { hashId: 'P29', default: 0 },
    OBJECTIVES_ROADMAP_ACTIVE_CATEGORIES:                                 { hashId: 'P30', default: [] },
    OBJECTIVES_ROADMAP_ACTIVE_FILTERS:                                    { hashId: 'P31', default: null },
    OBJECTIVES_SUPPRESS_REMOVAL_WARNING:                                  { hashId: 'P32', default: false },
    OBJECTIVES_SUPPRESS_REVERSION_WARNING:                                { hashId: 'P33', default: false },
    ADMIN_RESOURCES:                                                      { hashId: 'P34', default: 0 },
    ADMIN_RESOURCES_FEES:                                                 { hashId: 'P35', default: 0 },
    ADMIN_RESOURCES_HIRE_TYPES:                                           { hashId: 'P36', default: 0 },
    BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_YEARS:                            { hashId: 'P37', default: [] },
    BUDGET_VISUALIZATION_BY_HIRE_ACTIVE_FILTERS:                          { hashId: 'P38', default: null },
    BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_YEARS:                        { hashId: 'P39', default: [] },
    BUDGET_VISUALIZATION_BY_CATEGORY_ACTIVE_FILTERS:                      { hashId: 'P40', default: null },
    BUDGET_VISUALIZATION_BY_CATEGORY_EXPANDED_ROWS:                       { hashId: 'P41', default: [] },
    BUDGET_VISUALIZATION_BY_HIRE_HEADCOUNTS_VISIBLE:                      { hashId: 'P42', default: true },
    BUDGET_VISUALIZATION_BY_HEADCOUNT_HEADCOUNTS_VISIBLE:                 { hashId: 'P43', default: true },
    PROJECT_SUBSECTION:                                                   { hashId: 'P44', default: {} },
    HIRE_MANAGER_TAB:                                                     { hashId: 'P45', default: 1 },
    UI_DISPLAY_MODES:                                                     { hashId: 'P46', default: [] },
    OBJECTIVES_ALL_ITEMS_BY_CAT_ACTIVE_FILTERS:                           { hashId: 'P47', default: [] },
    OBJECTIVES_ROADMAP_TIMEFRAME_DATES:                                   { hashId: 'P48', default: [ null, null ] },
    BUDGET_VISUALIZATION_CUSTOM_BUDGET_ITEMS_VISIBLE:                     { hashId: 'P49', default: true },
    BUDGET_VISUALIZATION_BUDGET_ITEMS_VISIBLE:                            { hashId: 'P50', default: true },
    PROJECT_MANAGMENT_DETAILS_SECTION:                                    { hashId: 'P52', default: 0 },
    PROJECT_ID:                                                           { hashId: 'P53', default: 0 },
    PROJECT_PLANNING_REVIEW_SECTION:                                      { hashId: 'P54', default: 1 },
    OBJECTIVES_SELECTION_ALL_ITEMS_BY_SUBCAT_ACTIVE:                      { hashId: 'P55', default: [] },
    BUDGET_VISUALIZATION_BUDGET_PROFESSIONAL_FEE_ITEMS_VISIBLE:           { hashId: 'P56', default: true },
    BUDGET_VISUALIZATION_BUDGET_OPERATIONAL_COST_ITEMS_VISIBLE:           { hashId: 'P57', default: true },
    BUDGET_VISUALIZATION_BUDGET_ONGOING_COST_ITEMS_VISIBLE:               { hashId: 'P58', default: true },
    BUDGET_VISUALIZATION_BUDGET_RECURRING_PROFESSIONAL_FEE_ITEMS_VISIBLE: { hashId: 'P59', default: true },
    BUDGET_VISUALIZATION_BUDGET_RECURRING_OPERATIONAL_COST_ITEMS_VISIBLE: { hashId: 'P60', default: true },
    OBJECTIVES_ROADMAP_YEARS_IN_QUARTER_VIEW:                             { hashId: 'P62', default: [] },
    TOOLS_ACTIVITY_SEARCH_TABLE_STATE:                                    { hashId: 'P63', default: null },
    GLOBAL_ACTIVE_PROJECT_VISIBILITY:                                     { hashId: 'P64', default: [] },
    PROJECT_VERSION_ID:                                                   { hashId: 'P65', default: null },
    BUDGET_VISUALIZATION_BUDGET_SCHEDULED_COST_ITEMS_VISIBLE:             { hashId: 'P66', default: true },
    ADMIN_RESOURCES_BUSINESS_FUNCTIONS:                                   { hashId: 'P67', default: 0 },
    // BUDGET_VISUALIZATION_BUDGET_RECURRING_ONGOING_COST_ITEMS_VISIBLE:     { hashId: 'P61', default: false },
};

const defaultState: PreferenceStateType = _.mapObject( defaultCorvalPreferences, v => v.default );
const saveHashMap: KeyedObject = _.mapObject( defaultCorvalPreferences, v => v.hashId );

// Retrives storage name from environment
const getPrefsStorageKeyName = (): string => {
    return environment.session.cookies.prefs.namedKey;
};

// Retrives raw object value or an empty object
const getPrefsStorageValue = (): PrefState => {

    const prefStateCookie   = getPrefsStorageKeyName(),
          storedCookieValue = reactLocalStorage.getObject( prefStateCookie, {} );

    return recreateStateFromHashState( storedCookieValue );
};

// Retrieves stored prefs (if any) w/ defaults applied
const getStoredPrefs = (): PrefState => {
    return { ...defaultState, ...getPrefsStorageValue() };
};

// Retrieves a key from preferences, either from the key with the suffix specified, or a default one
const getPrefKey = ( pref: string | keyof PreferenceStateType, suffix?: string ): PrefKey => {
    return `${ pref }${ suffix ? `${ suffixDelimiter }${ suffix }` : '' }`;
};

// Shortens state object to save by hashing the keys from defaultPreferences using hashId property
const generateHashedState = ( state: PrefState ): object => {

    let newState: KeyedObject = {},
        omitKeys: string[]    = [];

    _.mapObject( state, ( v, k ) => {

        let [ prefKey, suffix = null ] = k.split( suffixDelimiter ),
            shortenedKey               = saveHashMap[ prefKey ],
            newPrefKey                 = getPrefKey( shortenedKey, suffix );

        // is this the same as the default?
        let defaultPref = _.findWhere( defaultCorvalPreferences, { hashId: shortenedKey } );

        if( defaultPref && v === defaultPref.default ){
            omitKeys.push( shortenedKey );
        }

        newState[ newPrefKey ] = v;
    } );

    // remove prefs that are == to default
    newState = _.omit( newState, omitKeys );

    return newState;
};

// Rehydrates saved object's keys by restoring from defaultPreferences using key of corresponding hashId property
const recreateStateFromHashState = ( state: object ): PrefState => {

    let newState: KeyedObject = {};

    _.mapObject( state, ( v, k ) => {

        let [ shortenedPrefKey, suffix = null ] = k.split( suffixDelimiter ),
            [ prefKey ]                         = getKeyByValue( saveHashMap, shortenedPrefKey ),
            newPrefKey                          = getPrefKey( prefKey, suffix );

        newState[ newPrefKey ] = v;
    } );

    return newState;
};

// Hook that allows operations on the preference state object. Use the suffix param to customize prefs by some scope (like a project id)
export const usePrefStates = ( _suffix: string = '', initialState: PrefState = {} ): PrefStateHook => {

    // if we're using an id or something, we need to
    // shrink it to a manageable size since:
    // ✓ id obfuscation is important
    // ✓ small storage limit
    let shortenedSuffix = _.isString( _suffix ) ? _.last( _suffix.split( '-' ) ) : '';

    // store the suffix for reuse
    let [ suffix ] = useState( shortenedSuffix );

    // create initial state
    let state: PrefState = {
        ...getStoredPrefs(),
        ...initialState,
    };

    let actionReducer = function reducer( state: PrefState, action: PrefStateAction ){

        let update = state;

        // the preference with a possible suffix set on hook init
        let prefKey = getPrefKey( action.value.pref, suffix );

        // store a version
        let storageValue  = reactLocalStorage.get( getPrefsStorageKeyName() ),
            storedPrefs   = storageValue ? JSON.parse( storageValue ) : {},
            previousPrefs = recreateStateFromHashState( storedPrefs );

        const updateChanges = {};

        try{
            switch( action.type ){

                case ACTIONS.UPDATE:

                    if( !_.isUndefined( previousPrefs[ prefKey ] ) ){

                        update = produce( previousPrefs, ( draft: any ) => {
                            draft[ prefKey ] = action.value.prefValue;
                            updateChanges[ prefKey ] = {
                                type:  'update',
                                key:   prefKey,
                                value: action.value.prefValue
                            };
                        } );

                    } else {

                        if( !_.isNull( action.value.pref ) ){

                            /* single key update */
                            update = { ...previousPrefs, [ prefKey ]: action.value.prefValue };
                            updateChanges[ prefKey ] = {
                                type:  'insert',
                                key:   prefKey,
                                value: action.value.prefValue
                            };

                        } else if( _.isObject( action.value.prefValue ) ){

                            /* WARNING: be careful, this bulk updates to the object,
                             and attempts to match a suffix if available */
                            let newPrefs = {};

                            _.mapObject( action.value.prefValue, ( value, key ) => {
                                newPrefs[ getPrefKey( key, suffix ) ] = value;
                                updateChanges[ getPrefKey( key, suffix ) ] = {
                                    type:  'insert-multi',
                                    key:   getPrefKey( key, suffix ),
                                    value: value
                                };
                                return value;
                            } );

                            update = { ...previousPrefs, ...newPrefs };
                        }
                    }

                    if( action.debug ){
                        console.log( `[PREFS]: Updating ${ prefKey } to:`, action.value.prefValue );
                    }

                    if( _.isFunction( action.beforeAction ) ){
                        update = action.beforeAction( update, updateChanges );
                    }

                    reactLocalStorage.setObject( getPrefsStorageKeyName(), generateHashedState( update ) );

                    if( typeof window !== 'undefined' ){
                        window.dispatchEvent( new Event( 'storage' ) );
                    }

                    return update;

                case ACTIONS.RESET:

                    if( action.debug ){
                        console.log( `[PREFS]: Prefs reset! Previous values:` );
                        console.log( previousPrefs );
                    }

                    reactLocalStorage.setObject( getPrefsStorageKeyName(), getStoredPrefs() );

                    return previousPrefs;

                case ACTIONS.CLEAR:

                    if( action.debug ){
                        console.log( `[PREFS]: Prefs removed! Previous values:` );
                        console.log( previousPrefs );
                    }

                    reactLocalStorage.remove( getPrefsStorageKeyName() );

                    return previousPrefs;

                default:

                    throw new Error();
            }

        } catch( e ) {

            console.warn( e );
        }

        return previousPrefs;
    };

    let prefEnums = _.object(
        _.keys( defaultState ),
        _.keys( defaultState )
    ) as PrefEnumList;

    const [ _state, _dispatch ] = useReducer( actionReducer, state, undefined );

    let actions: PrefStateHookActions = {

        'get': ( pref, debug = false, beforeAction = null ) => {

            // get prefkey that was stored and a fresh copy of current local storage
            let prefKey: PrefKey         = getPrefKey( pref, suffix ),
                currenState: KeyedObject = getStoredPrefs();

            if( debug ){

                console.log( `[PREFS]: Getting ${ prefKey }:`, currenState[ prefKey ] );
            }

            // if the prefkey is found (read: defined), retrieve that...
            if( !_.isUndefined( currenState[ prefKey ] ) ){

                return ( _.isFunction( beforeAction ) )
                       ? beforeAction( currenState[ prefKey ] )
                       : currenState[ prefKey ];
            }

            // otherwise try to retrieve regular version as default / non pref'ed value
            return currenState[ pref ];
        },

        'update': ( pref, prefValue, debug = false, beforeAction = null ) => _dispatch( { type: ACTIONS.UPDATE, value: { pref, prefValue }, debug, beforeAction } ),
        'reset':  ( debug = false ) => _dispatch( { type: ACTIONS.RESET, value: null, debug } ),
        'clear':  ( debug = false ) => _dispatch( { type: ACTIONS.CLEAR, value: null, debug } ),

    };

    // [ PrefState, PrefStateHookActions, PrefEnumList ]
    return [ _state, actions, prefEnums ];
};
